<template>
  <div
    id="search"
    @keydown.esc.exact.prevent="closeModal"
  >
    <div
      ref="modalContainer"
      class="search-container"
    >
      <button
        ref="closeButton"
        class="close-button"
        :aria-label="$t('search.closeSearchModal')"
        @click="closeModal"
      >
        <Icon name="lucide:x" />
        {{ $t('search.closeSearch') }}
      </button>
      <SearchBar
        :initial-search-term="route.query.q"
        :search-suggestions="searchSuggestions"
        @search-term="debouncedHandleSuggestions"
        @full-search-initiated="debouncedShowSearchResults"
      />
      <SearchResults
        v-if="showResults"
        :results="searchResults"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, useTemplateRef } from 'vue';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useStore } from 'vuex';
import SearchBar from './SearchBar.vue';
import SearchResults from './SearchResults.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { value: authToken } = useCookie('auth-token');

const modalContainer = useTemplateRef('modalContainer');
const { deactivate } = useFocusTrap(modalContainer, { immediate: true });

const showResults = ref(false);
const searchSuggestions = ref([]);
const searchResults = ref([]);

const closeModal = () => {
  const currentQuery = { ...route.query };
  delete currentQuery.openSearch;
  deactivate();
  return navigateTo({
    path: route.path,
    query: currentQuery,
  });
};

onClickOutside(modalContainer, closeModal);

// Handle search and result states.
async function fetchSearchResults(searchTerm) {
  const { data, status, error, refresh, execute, clear } = await useAsyncData(
    `is-${searchTerm}`,
    () => {
      return store.dispatch('getSearchResults', {
        term: searchTerm,
        writableApi: useRuntimeConfig().public.WRITABLE_BASE_API_URL,
        searchByCurrentOrg: useRuntimeConfig().public.IS_INTRANET,
        // selectedFilters: filterTypes,
        token: authToken,
      });
    },
    {
      immediate: false,
    },
  );

  return {
    data,
    status,
    error,
    refresh,
    execute,
    clear,
  };
}

const debouncedHandleSuggestions = useDebounceFn(handleSuggestions, 300);
const debouncedShowSearchResults = useDebounceFn(showSearchResults, 300);

async function handleSuggestions(searchTerm) {
  if (!searchTerm || searchTerm === '') {
    router.push({
      query: { ...route.query, q: '' },
    });
    searchSuggestions.value = [];
    showResults.value = false;
    return;
  }

  const { data, error, execute } = await fetchSearchResults(searchTerm);
  // const selectedFilterTypes = filterOptions.value.filter((option) => selectedFilters.value.includes(option.id));
  // const filterTypes = selectedFilterTypes.map((filter) => filter.filter_type);
  //
  try {
    await execute();
    searchSuggestions.value = data.value.data.results.slice(0, 3);
    router.push({
      query: { ...route.query, q: searchTerm },
    });
  } catch {
    console.error(error);
  }
}

async function showSearchResults(searchTerm) {
  if (!searchTerm || searchTerm === '') {
    router.push({
      query: { ...route.query, q: '' },
    });
    showResults.value = false;
    searchResults.value = [];
    return;
  }
  const { data, error, execute } = await fetchSearchResults(searchTerm);

  try {
    await execute();
    showResults.value = true;
    searchSuggestions.value = [];
    searchResults.value = data.value.data.results;
    router.push({
      query: { ...route.query, q: searchTerm },
    });
  } catch {
    console.error(error);
  }
}

await showSearchResults(route.query.q);
</script>

<style lang="scss" scoped>
@import '../global/scss/mixins/_breakpoints.scss';

#search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: rgba(34, 34, 34, 0.8);
  z-index: 10;

  .search-container {
    width: 48vw;
    height: 64vh;
    background: transparent;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;
    position: relative;
  }
}

.close-button {
  opacity: 1;
  margin-left: auto;
  display: flex;
  gap: 4px;
  align-items: center;
  background: none;
  padding: 5px;
  border: none;
  color: var(--primary-white);

  font-size: 14px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.6px;

  &:hover {
    cursor: pointer;
  }
  &:focus-visible {
    outline-offset: 2px;
  }
}

@include screen('<=md') {
  #search {
    --search-bar-height: 74px;
    .search-container {
      position: absolute;
      top: 0;
      padding: 16px 24px;
      width: calc(100vw - 48px);
      height: calc(100vh - 48px);

      .search-bar {
        height: var(--search-bar-height);
      }

      .search-results {
        height: calc(100vh - var(--search-bar-height));
      }
    }
  }
}

@include screen('xs') {
  #search .search-container {
    --search-bar-height: 74px;
    width: 100%;
    height: 100%;
    gap: 0;
    padding: 0;
    .search-bar {
      height: var(--search-bar-height);
      border-radius: 0;
    }
    .search-results {
      border-radius: 0;
      height: calc(100vh - var(--search-bar-height));
    }
    .close-button {
      margin: 6px 6px 7px 0;
      margin-left: auto;
    }
  }
}
</style>
